import { Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import { useTodayQueue } from '@/features/appointments';
import { Button } from '@/shared/ui/Button';
import { cn } from '@/shared/lib/utils';
import { HEADER_CHIP_CLASS, HEADER_CHIP_ICON_WELL } from './header-chrome';
import { pickNextUp } from './header-next-up';

/**
 * Compact next-up chip — time + first name.
 */
export function HeaderNextUp({ className }: { className?: string }): JSX.Element | null {
  const { data } = useTodayQueue();

  const next = useMemo(() => pickNextUp(data?.queue ?? []), [data?.queue]);

  const soon = useMemo(() => {
    if (!next) return false;
    const ms = new Date(next.scheduledAt).getTime() - Date.now();
    return ms <= 15 * 60_000 && ms >= -15 * 60_000;
  }, [next]);

  if (!next) return null;

  const label = `Next at ${next.timeLabel}: ${next.patientFirstName}`;

  return (
    <Button
      type="button"
      variant="ghost"
      asChild
      className={cn(
        HEADER_CHIP_CLASS,
        'hidden sm:inline-flex',
        soon && 'bg-brand-50/90 hover:bg-brand-50',
        className,
      )}
    >
      <Link to={`/appointments/${next.id}`} title={label} aria-label={label}>
        <span className={HEADER_CHIP_ICON_WELL}>
          <Clock className="size-3.5" aria-hidden />
        </span>
        <span className="tabular-nums text-ink-800">{next.timeLabel}</span>
        <span className="max-w-[4.5rem] truncate text-ink-500 font-medium">
          {next.patientFirstName}
        </span>
      </Link>
    </Button>
  );
}
